// StatementsFilterBar.tsx — Sprint 13. Filter mode select + the
// matching mode-specific picker (month / quarter / range) +
// search input.
import React from 'react';
import { Card, Col, DatePicker, Input, Row, Select, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { FilterOutlined } from '@ant-design/icons';
import { monthOptions, quarterOptions, type FilterMode } from './statementHelpers';

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

export interface StatementsFilterBarProps {
  mode:            FilterMode;
  selectedMonth:   string;
  selectedQuarter: string;
  dateRange:       [Dayjs, Dayjs] | null;
  searchText:      string;

  onModeChange:     (m: FilterMode) => void;
  onMonthChange:    (m: string) => void;
  onQuarterChange:  (q: string) => void;
  onRangeChange:    (r: [Dayjs, Dayjs] | null) => void;
  onSearchChange:   (s: string) => void;
}

export const StatementsFilterBar: React.FC<StatementsFilterBarProps> = ({
  mode, selectedMonth, selectedQuarter, dateRange, searchText,
  onModeChange, onMonthChange, onQuarterChange, onRangeChange, onSearchChange,
}) => (
  <Card
    size="small"
    style={{
      marginBottom: 16,
      borderRadius: 10,
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}
    bodyStyle={{ padding: '14px 20px' }}
  >
    <Row gutter={16} align="middle">
      <Col>
        <Text strong style={{ marginRight: 8 }}>
          <FilterOutlined /> Filter by:
        </Text>
        {/* Sprint 146 (R14-13) — "All" is new AND the default. See
            statementHelpers.FilterMode for why. */}
        <Select value={mode} onChange={onModeChange} style={{ width: 130 }}>
          <Option value="all">All</Option>
          <Option value="monthly">Monthly</Option>
          <Option value="quarterly">Quarterly</Option>
          <Option value="range">Date Range</Option>
        </Select>
      </Col>

      {mode === 'monthly' && (
        <Col>
          <Select
            showSearch
            value={selectedMonth}
            onChange={onMonthChange}
            style={{ width: 180 }}
            options={monthOptions()}
            optionFilterProp="label"
          />
        </Col>
      )}

      {mode === 'quarterly' && (
        <Col>
          <Select
            value={selectedQuarter}
            onChange={onQuarterChange}
            style={{ width: 150 }}
            options={quarterOptions()}
          />
        </Col>
      )}

      {mode === 'range' && (
        <Col>
          <RangePicker
            value={dateRange ?? undefined}
            onChange={(vals) => onRangeChange(vals as [Dayjs, Dayjs] | null)}
            style={{ width: 260 }}
          />
        </Col>
      )}

      <Col flex="auto">
        <Input.Search
          placeholder="Search by owner name, unit, or period…"
          allowClear
          value={searchText}
          onChange={e => onSearchChange(e.target.value)}
          style={{ maxWidth: 320, float: 'right' }}
        />
      </Col>
    </Row>
  </Card>
);
